// Locale-to-Currency detection utility for Timelining workspace creation

const REGION_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  IN: 'INR',
  JP: 'JPY',
  CN: 'CNY',
  CA: 'CAD',
  AU: 'AUD',
  SG: 'SGD',
  AE: 'AED',
  CH: 'CHF',
  HK: 'HKD',
  KR: 'KRW',
  BR: 'BRL',
  MX: 'MXN',
  ZA: 'ZAR',
  NZ: 'NZD',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  // Eurozone countries
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  EE: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  CY: 'EUR',
  MT: 'EUR',
  LU: 'EUR',
}

const LANGUAGE_TO_CURRENCY: Record<string, string> = {
  ja: 'JPY',
  ko: 'KRW',
  hi: 'INR',
}

/**
 * Detects the user's preferred currency based on browser / runtime locale.
 * Follows §2.8 rules:
 * 1. Resolves locale from argument, navigator.language, or Intl runtime default.
 * 2. Matches region code against canonical ISO currencies.
 * 3. Falls back to language-based inference.
 * 4. Defaults to 'USD' when unknown or missing.
 */
export function detectLocaleCurrency(localeOverride?: string): string {
  let locale = localeOverride
  if (!locale && typeof navigator !== 'undefined' && navigator.language) {
    locale = navigator.language
  }
  if (!locale && typeof Intl !== 'undefined') {
    locale = new Intl.NumberFormat().resolvedOptions().locale
  }

  if (!locale || typeof locale !== 'string') {
    return 'USD'
  }

  const clean = locale.trim()
  const parts = clean.replace(/_/g, '-').split('-')

  // If there is a region subtag (e.g. en-US, hi-IN, de-DE)
  if (parts.length >= 2) {
    const region = parts[parts.length - 1].toUpperCase()
    if (REGION_TO_CURRENCY[region]) {
      return REGION_TO_CURRENCY[region]
    }
  }

  // Language primary tag (e.g. ja, ko, hi)
  const lang = parts[0].toLowerCase()
  if (LANGUAGE_TO_CURRENCY[lang]) {
    return LANGUAGE_TO_CURRENCY[lang]
  }

  return 'USD'
}
